import { memo, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { LexIcon } from "../../../../assets/icons/lex";
import useIsScrolled from "../../../hooks/useIsScrolled";
import { getModelById } from "../../../services";
import { getQueryValue } from "../../../store/selectors";
import {
  downloadFile,
  getHighlightedExcerpts,
  handleClickNote,
  handleInternalSpaNavigation,
  printContent,
  processDocumentText,
} from "../../../utils";
import { saveDocumentWithNotify } from "../../../utils/_handleSaveDocument";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal from "../../components/global/_notesModal";
import ModelDetailsHeaderActions from "./components/ModelDetailsHeaderActions";
import "./index.scss";

const HtmlContent = memo(({ html }) => (
  <div className="content-text" dangerouslySetInnerHTML={{ __html: html }} />
));

const ModelDetails = (props) => {
  const { id } = useParams();

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [modalType, setModalType] = useState("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const isScrolled = useIsScrolled();

  const query = useSelector(getQueryValue);
  const dispatch = useDispatch();

  const contentHtml = useMemo(() => {
    if (!model?.texto) return "";
    return query
      ? getHighlightedExcerpts(model.texto, query, model.texto.length, false)
      : model.texto;
  }, [model?.texto, query]);

  const handleOpenFindBar = () => {
    window.dispatchEvent(new CustomEvent("pdl:openFindBar"));
  };

  const handleDownloadFile = () => {
    const wordUrl = `https://produtos.lex.com.br${model.versaoWord}`;

    downloadFile({
      name: model.titulo || "arquivo.docx",
      url: wordUrl,
    });
  };

  const handlePrint = () =>
    printContent({
      text: model?.texto,
      title: model?.titulo,
    });

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: model?._id ?? "",
      id: model?.id,
      tipo: "Modelos",
      url: window.location.href,
    });

  useEffect(() => {
    const loadModel = async () => {
      setShowNoteModal(false);
      try {
        setLoading(true);
        setError(null);

        const response = await getModelById(id);

        if (response && response.texto) {
          response.texto = processDocumentText({ htmlText: response.texto });
        }

        setModel(response);
      } catch (err) {
        const status = err?.response?.status;

        setError(
          status === 404
            ? "Estamos trabalhando para disponibilizá-lo em breve."
            : err?.response?.data?.message ||
                "Não foi possível encontrar o modelo",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadModel();
    }
  }, [id]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const rawTarget = event.target;

      const target =
        rawTarget.closest?.("[data-nota-link], .nota-super, a") || rawTarget;

      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const match = href.match(/(art\d+)/i);

        if (match) {
          event.preventDefault();

          const articlePart = match[1];

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];

          if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
            cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event)) {
            return;
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [model, showNoteModal]);

  if (loading) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando modelo..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <LexIcon />
                <PDLTypography size="xl" variant="primary" weight="medium">
                  Este documento ainda não está disponível.
                </PDLTypography>
                <p className="text-muted">{error}</p>
                <p className="text-muted">
                  Se for urgente, fale com a gente. Teremos prazer em ajudar.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props} showHeader headerClass="details-sticky">
      <Container>
        <Row>
          <Col>
            <ModelDetailsHeaderActions
              title={model?.titulo || "Modelo"}
              isScrolled={isScrolled}
              isSavingDocument={isSavingDocument}
              onOpenFindBar={handleOpenFindBar}
              onSaveDocument={handleSaveDocument}
              onPrint={handlePrint}
              onDownloadWord={handleDownloadFile}
            />
            <div className="mt-4">
              <div className="content-box">
                {model?.texto ? (
                  <HtmlContent html={contentHtml} />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>
            </div>
          </Col>
          <NotesModal
            noteContent={""}
            noteTitle={""}
            setShowNoteModal={setShowNoteModal}
            showNoteModal={showNoteModal}
            type={modalType}
            articleId={articleId}
            url={articleUrl}
          />
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default ModelDetails;
